import { useMemo, type ReactNode } from 'react'
import { createPortal } from 'react-dom'
import { PanelBottom } from 'lucide-react'
import { useAppStore } from '@core/store'
import {
  useDetachedWindow,
  type DetachedWindowHandlers,
} from '@core/layout/janela-destacada/useDetachedWindow'

/** Nome próprio da janela dos módulos. Diferente do das vistas 2D de
 *  propósito: com o mesmo nome, destacar o BCF deitava abaixo o desenho
 *  2D que estivesse destacado (o browser reaproveita a janela). */
export const MODULE_WINDOW_NAME = 'vn2r-module'
export const MODULE_WINDOW_TITLE = 'VN2R openBIM Hub · Módulo'
export const MODULE_WINDOW_FEATURES =
  'width=1100,height=780,resizable=yes,scrollbars=yes'

/**
 * Mostra o módulo activo numa janela real do browser.
 *
 * O mecanismo é o mesmo `useDetachedWindow` das vistas 2D (window.open +
 * clonagem das folhas de estilo + `createPortal`), agora partilhado em
 * vez de duplicado: a árvore React continua a ser a da página principal,
 * por isso o módulo mantém o store do zustand e o contexto do React
 * Router. Navegar dentro da janela muda o URL da página principal e a
 * janela volta a desenhar o ecrã novo.
 *
 * Se a janela for bloqueada ou o utilizador a fechar, o módulo volta ao
 * dock: voltar para `full` deixava o utilizador outra vez com o 3D
 * escondido, que é exactamente o estado de que se queixava.
 */
function ModulePopup({ children }: { children: ReactNode }) {
  const setMode = useAppStore((s) => s.setWorkspaceLayoutMode)
  const pushToast = useAppStore((s) => s.pushToast)

  const handlers = useMemo<DetachedWindowHandlers>(
    () => ({
      onBlocked: () => {
        pushToast(
          'O browser bloqueou a janela. Permite janelas para este sítio e tenta de novo.',
        )
        setMode('bottom')
      },
      onClose: () => setMode('bottom'),
    }),
    [setMode, pushToast],
  )

  const { container } = useDetachedWindow(true, handlers, {
    name: MODULE_WINDOW_NAME,
    title: MODULE_WINDOW_TITLE,
    features: MODULE_WINDOW_FEATURES,
  })

  if (!container) return null

  return createPortal(
    <>
      <div className="flex h-8 shrink-0 items-center justify-between border-b border-[var(--color-border)] bg-[var(--color-panel)] px-2 text-xs">
        <span className="truncate text-[var(--color-text-muted)]">
          VN2R openBIM Hub · módulo destacado
        </span>
        <button
          type="button"
          onClick={() => setMode('bottom')}
          title="Acoplar ao viewer"
          className="flex items-center gap-1 rounded border border-[var(--color-border)] px-2 py-0.5 text-[10px] text-[var(--color-text-muted)] hover:text-[var(--color-text)]"
        >
          <PanelBottom size={11} />
          Acoplar ao viewer
        </button>
      </div>
      <div className="flex min-h-0 flex-1 overflow-auto bg-[var(--color-bg)]">
        {children}
      </div>
    </>,
    container,
  )
}

export default ModulePopup
